import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../core/Auth';
import { getUserByToken } from '../core/_requests';
import axios from 'axios';
import { toAbsoluteUrl } from '../../../../_metronic/helpers';
import { useThemeMode } from '../../../../_metronic/partials';

export function LekkoCallback() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { saveAuth, setCurrentUser } = useAuth();
    const [error, setError] = useState<string | null>(null);
    const { mode } = useThemeMode();
    const effectRan = useRef(false);

    useEffect(() => {
        if (effectRan.current) return;
        effectRan.current = true;

        const handleCallback = async () => {
            const code = searchParams.get('code');
            if (!code) {
                setError('Código de autorização não encontrado.');
                return;
            }

            // Remove o código da URL imediatamente para evitar reuso caso o React renderize de novo ou o usuário dê F5
            window.history.replaceState({}, document.title, window.location.pathname);

            const verifier = sessionStorage.getItem('lekko_pkce_verifier');
            if (!verifier) {
                setError('Falha na validação de segurança (PKCE verifier ausente na sessão).');
                return;
            }

            try {
                const redirectUri = `${window.location.origin}/auth/lekko/callback`;

                // 1. Troca o code pelo token diretamente no Keycloak
                const tokenUrl = process.env.REACT_APP_LEKKO_TOKEN_URL || 'https://account.lekko.com.br/realms/lekko/protocol/openid-connect/token';
                const clientId = process.env.REACT_APP_KEYCLOAK_CLIENT_ID || 'solar-vision';

                const params = new URLSearchParams({
                    client_id: clientId,
                    grant_type: 'authorization_code',
                    code,
                    redirect_uri: redirectUri,
                    code_verifier: verifier,
                });

                const keycloakResponse = await axios.post(tokenUrl, params.toString(), {
                    headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
                });

                const accessToken = keycloakResponse.data.access_token;

                // 2. Envia apenas o token para o nosso backend validar e gerar a sessão local
                const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3333/api/v1/auth';
                const exchangeResponse = await axios.post(`${API_URL}/lekko/exchange`, {
                    access_token: accessToken
                });

                const auth = exchangeResponse.data;
                saveAuth(auth);

                // 3. Busca os dados do usuário com o novo token e salva no contexto
                const { data: user } = await getUserByToken(auth.api_token);
                setCurrentUser(user);

                // Limpa o verifier da memória
                sessionStorage.removeItem('lekko_pkce_verifier');

                // Redireciona para o dashboard
                window.location.href = '/';
            } catch (err: any) {
                console.error('Erro na autenticação com Lekko:', err);
                const message = err.response?.data?.message || err.message || 'Erro desconhecido';
                setError(`Falha ao realizar login: ${message}`);
            }
        };

        handleCallback();
    }, [searchParams, saveAuth, setCurrentUser]);

    if (error) {
        return (
            <div className='d-flex flex-column flex-center text-center p-10'>
                <div className='card card-flush w-md-650px py-5'>
                    <div className='card-body py-15 py-lg-20'>
                        <h1 className='fw-bolder fs-2hx text-gray-900 mb-4'>Falha no Login Lekko</h1>
                        <div className='fw-semibold fs-6 text-gray-500 mb-7'>{error}</div>
                        <div className='mb-3'>
                            <button onClick={() => navigate('/auth/login')} className='btn btn-sm btn-primary'>
                                Voltar para o Login
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className='d-flex flex-column flex-center text-center p-10'>
            <div className='card card-flush w-md-650px py-5'>
                <div className='card-body py-15 py-lg-20'>
                    <div className='mb-7'>
                        <img
                            alt='Logo'
                            src={toAbsoluteUrl(mode === 'dark' ? '/media/imgs/novalogo.png' : '/media/imgs/novaLogoBlack.png')}
                            className='h-60px'
                        />
                    </div>
                    <h1 className='fw-bolder text-gray-900 mb-5'>Validando acesso...</h1>
                    <div className='fw-semibold fs-6 text-gray-500 mb-7'>
                        Por favor, aguarde enquanto autenticamos sua conta Lekko.
                    </div>
                    <div className='spinner-border text-primary' role='status'>
                        <span className='visually-hidden'>Carregando...</span>
                    </div>
                </div>
            </div>
        </div>
    );
}
